"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, FileSearch, Layers, ChartLine as LineChart, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const processSteps = [{
  stepNumber: "01",
  badge: "Phase 1: Initial 30 Days",
  title: "Strategic SEO Audit and Planning",
  icon: FileSearch,
  description: <>
        Every engagement starts with a comprehensive{" "}
        <Link to="/SeoAuditsPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
          SEO audit
        </Link>{" "}
        measuring your current search footprint against commercial goals. I inspect technical
        foundations to resolve crawl errors, site speed, indexability, sitemap health, and Core Web
        Vitals bottlenecks. Within 30 days, I pinpoint quick wins and long-term search growth
        levers, establishing reliable analytics and KPI tracking so every future decision is backed
        by verifiable search data.
      </>,
  deliverables: ["Technical health & Core Web Vitals audit", "Immediate quick wins & prioritized fix list", "Baseline tracking, goal setup & 90-day roadmap"]
}, {
  stepNumber: "02",
  badge: "Phase 2: Execution Architecture",
  title: "Integrated SEO Strategy Development",
  icon: Layers,
  description: <>
        I build a structured organic strategy aligned directly with your sales funnel and customer
        decision journey. This includes buyer-intent keyword targeting, topical authority clusters,
        and high-converting comparison architecture. I prepare comprehensive technical roadmaps
        (structured schema, mobile parity, site architecture) and strengthen{" "}
        <Link to="/LocalSEOPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
          Local SEO
        </Link>{" "}
        signals like Google Business Profile and regional landing pages where relevant.
      </>,
  deliverables: ["High-intent keyword & topical cluster mapping", "Content briefs & conversion-focused page blueprints", "Structured data, internal linking & local signals"]
}, {
  stepNumber: "03",
  badge: "Phase 3: Continuous Growth",
  title: "Ongoing Strategic Leadership",
  icon: LineChart,
  description: <>
        SEO yields compounded returns through systematic governance. I provide direct strategic
        oversight, leading regular review sessions and guiding your in-house marketing specialists,
        copywriters, and developers. Through weekly collaboration, monthly performance reports, and
        quarterly roadmap adjustments, you receive an objective partner who evaluates what works,
        eliminates wasted effort, and redirects resources to the highest-yield search opportunities.
      </>,
  deliverables: ["Direct technical and content steering for internal teams", "Transparent monthly reporting & pipeline metrics", "Quarterly strategic reviews & algorithmic adaptations"]
}];
export default function Process() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="howprocessworks" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex justify-center">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary border border-border">
              <Sparkles className="mr-1.5 size-3.5 text-primary" />
              Structured Methodology
            </Badge>
          </motion.div>

          <motion.h2 initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="mt-4 font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            How My Fractional SEO Process Works
          </motion.h2>

          <motion.p initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed font-sans">
            Generating sustainable search growth does not require complicated jargon or agency
            bloat. My fractional engagement model follows a clear, disciplined three-step structure
            where you collaborate directly with me at every stage.
          </motion.p>
        </div>

        {/* Steps Timeline Grid */}
        <div className="mt-16 relative">
          {/* Vertical Connecting Line for Desktop */}
          <div aria-hidden="true" className="hidden lg:block absolute left-1/2 top-8 bottom-8 w-px -translate-x-1/2 bg-border" />

          <div className="space-y-12 lg:space-y-16">
            {processSteps.map((step, index) => {
            const StepIcon = step.icon;
            const isEven = index % 2 === 1;
            return <div key={step.stepNumber} data-index={index} className={`relative flex flex-col lg:items-center ${isEven ? "lg:flex-row-reverse" : "lg:flex-row"} gap-8 lg:gap-16`}>
                  {/* Timeline Badge Node (Desktop Center) */}
                  <div aria-hidden="true" className="hidden lg:flex absolute left-1/2 -translate-x-1/2 size-12 rounded-full border-2 border-primary bg-background shadow-sm items-center justify-center z-10">
                    <span className="font-serif font-bold text-sm text-primary">
                      {step.stepNumber}
                    </span>
                  </div>

                  {/* Main Step Card */}
                  <div className="w-full lg:w-[calc(50%-2rem)]">
                    <motion.div initial={prefersReducedMotion ? {
                  opacity: 1,
                  y: 0
                } : {
                  opacity: 0,
                  y: 24
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true,
                  amount: 0.2
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: "easeOut"
                }}>
                      <Card className="border border-border/80 bg-card shadow-sm hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="inline-flex size-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                              <StepIcon className="size-5" />
                            </div>
                            <Badge variant="outline" className="border-primary/20 text-primary font-medium text-xs bg-primary/5">
                              {step.badge}
                            </Badge>
                          </div>

                          <div className="mt-4 flex items-baseline gap-2">
                            <span className="lg:hidden font-serif font-bold text-xs text-primary px-2 py-0.5 rounded bg-primary/10">
                              Step {step.stepNumber}
                            </span>
                          </div>

                          <CardTitle className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                            <h3 className="text-xl sm:text-2xl font-bold font-serif leading-snug">
                              {step.title}
                            </h3>
                          </CardTitle>
                          <CardDescription className="text-sm font-sans text-muted-foreground leading-relaxed pt-2">
                            {step.description}
                          </CardDescription>
                        </CardHeader>

                        <CardContent className="pt-0">
                          <Separator className="my-4 bg-border" />
                          <p className="text-xs font-semibold uppercase tracking-wider text-foreground">
                            Key Deliverables
                          </p>
                          <ul className="mt-3 space-y-2">
                            {step.deliverables.map((item, itemIdx) => <li key={itemIdx} data-index={itemIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-muted-foreground">
                                <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                                <span>{item}</span>
                              </li>)}
                          </ul>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </div>

                  {/* Empty Spacer Column for Desktop Balance */}
                  <div className="hidden lg:block w-[calc(50%-2rem)]" />
                </div>;
          })}
          </div>
        </div>

        {/* Bottom Banner & Conversion CTA */}
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-16 md:mt-20 rounded-2xl border border-border bg-card p-8 md:p-12 text-center shadow-sm">
          <div className="mx-auto max-w-2xl">
            <h3 className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              No guesswork. No wasted effort.
            </h3>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
              Senior-level search leadership embedded into your business. I guide your strategy, align your team, and drive measurable revenue—without agency overhead or the cost of a full-time executive hire.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium hover:border-primary/40">
                <Link to="/CaseStudiesPage">Explore Case Studies</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}